import React from 'react';
import { MapPinIcon, UserIcon, UserPlusIcon, CurrencyDollarIcon, CubeIcon, ExclamationCircleIcon, CheckCircleIcon, XCircleIcon, EyeIcon, SparklesIcon } from '@heroicons/react/24/outline';
import type { PainelRegiao } from '../../types/pedidos';

interface KanbanBoardProps {
  layout: string;
  regioes: string[];
  porRegiao: Record<string, PainelRegiao[]>;
  onEditRegionClick: (pedido: PainelRegiao) => void;
  onPedidoClick: (pedido: PainelRegiao) => void;
  isFullscreen: boolean;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  layout,
  regioes,
  porRegiao,
  onPedidoClick,
  // isFullscreen não usado nesta implementação básica
}) => {
  const getStatusBorderColor = (status: string): string => {
    const trimmedStatus = status?.trim().toLowerCase()
    switch (trimmedStatus) {
      case 'conferido':
      case 'sim':
        return 'border-l-emerald-500'
      case 'pendente':
      case 'nao':
        return 'border-l-amber-300'
      case 'parcial':
        return 'border-l-rose-500'
      default:
        return 'border-l-zinc-400'
    }
  }

  const getStatusIcon = (status: string) => {
    const trimmedStatus = status?.trim().toLowerCase()
    switch (trimmedStatus) {
      case 'conferido':
      case 'sim':
        return <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
      case 'pendente':
      case 'nao':
        return <ExclamationCircleIcon className="h-4 w-4 text-amber-300" />
      case 'parcial':
        return <XCircleIcon className="h-4 w-4 text-rose-500" />
      default:
        return <ExclamationCircleIcon className="h-4 w-4 text-zinc-400" />
    }
  }

  const isNewPedido = (dataInclusao: string): boolean => {
    const hoje = new Date()
    const ontem = new Date(hoje)
    ontem.setDate(hoje.getDate() - 1)
    
    const dataPedido = new Date(dataInclusao)
    
    // Verifica se foi criado hoje ou ontem
    return dataPedido >= ontem
  }
  return (
    <div className={`flex ${
      layout === 'vertical' ? 'flex-col gap-6' : 'flex-row gap-6 overflow-x-auto pb-4'
    }`}>
      {regioes.map((regiao) => (
        <div 
          key={regiao} 
          className={`${
            layout === 'vertical' 
              ? 'w-full mb-4' 
              : 'w-80 md:w-96 min-w-[300px] md:min-w-[380px] flex-shrink-0'
          } rounded-xl border border-zinc-200 bg-white/70 backdrop-blur-sm shadow-lg shadow-zinc-200/50 p-3 md:p-4 dark:border-zinc-700 dark:bg-zinc-800/70 dark:shadow-zinc-900/20`}
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPinIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <h3 className="text-sm font-bold text-zinc-800 dark:text-zinc-200">
                {regiao}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <CubeIcon className="h-3 w-3 text-zinc-500" />
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-blue-600 px-2 text-xs font-semibold text-white shadow-sm">
                {porRegiao[regiao]?.length || 0}
              </span>
            </div>
          </div>
          
          <div className="space-y-4">
            {porRegiao[regiao]?.map((pedido, idx) => (
              <div
                key={`${pedido.codigo}-${idx}`}
                className={`group relative rounded-lg bg-white border border-zinc-200 border-l-4 ${getStatusBorderColor(pedido.conferido || '')} p-4 transition-all duration-200 hover:shadow-md hover:shadow-zinc-200/50 dark:bg-zinc-800 dark:border-zinc-700 cursor-pointer`}
                onClick={() => onPedidoClick(pedido)}
              >
                {/* Header do Card */}
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    {getStatusIcon(pedido.conferido || '')}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm">
                          Pedido #{pedido.codigo || 'S/N'}
                        </span>
                        {pedido.empresa && (
                          <span
                            className="inline-flex items-center rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300"
                            title="Empresa do pedido"
                          >
                            Emp {pedido.empresa}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">
                        {new Date(pedido.data).toLocaleDateString('pt-BR')} • {pedido.hora?.split('.')[0] || ''}
                      </div>
                    </div>
                  </div>

                  {/* Badge NEW para pedidos recentes */}
                  {pedido.data && isNewPedido(pedido.data) && (
                    <div className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-blue-500 px-2 py-1 text-xs font-medium text-white">
                      <SparklesIcon className="h-3 w-3" />
                      Novo
                    </div>
                  )}
                </div>
                
                {/* Informações Principais */}
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-2 text-zinc-700 dark:text-zinc-300">
                    <UserIcon className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                    <span className="font-medium truncate text-xs">{pedido.cliente || 'Cliente não informado'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-zinc-600 dark:text-zinc-400">
                    <UserPlusIcon className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                    <span className="truncate text-xs">{pedido.vendedor || 'Vendedor não informado'}</span>
                  </div>
                  
                  {pedido.cidade && (
                    <div className="flex items-center gap-2 text-zinc-600 dark:text-zinc-400">
                      <MapPinIcon className="h-3.5 w-3.5 text-rose-500 flex-shrink-0" />
                      <span className="truncate text-xs">{pedido.cidade}</span>
                    </div>
                  )}
                  
                  {pedido.endereco && (
                    <div className="flex items-start gap-2 text-xs text-zinc-500 dark:text-zinc-500">
                      <MapPinIcon className="h-3 w-3 mt-0.5 text-zinc-400 flex-shrink-0" />
                      <span className="truncate text-xs">{pedido.endereco}, {pedido.numero || 'S/N'}</span>
                    </div>
                  )}
                </div>

                {/* Valor do Pedido */}
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">
                    {pedido.conferido?.trim() === 'SIM' 
                      ? 'Conferido' 
                      : pedido.conferido?.trim() === 'PARCIAL' 
                      ? 'Parcialmente conferido'
                      : 'Pendente de conferência'
                    }
                  </div>
                  
                  {pedido.valorpedido && (
                    <div className="flex items-center gap-1 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                      <CurrencyDollarIcon className="h-4 w-4 text-emerald-600" />
                      {parseFloat(pedido.valorpedido.toString()).toLocaleString('pt-BR', { 
                        style: 'currency', 
                        currency: 'BRL' 
                      })}
                    </div>
                  )}
                </div>

                {/* Observação */}
                {pedido.observacao && (
                  <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-700">
                    <div className="flex items-start gap-2">
                      <EyeIcon className="h-3 w-3 mt-0.5 text-zinc-400 flex-shrink-0" />
                      <span className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
                        {pedido.observacao}
                      </span>
                    </div>
                  </div>
                )}

              </div>
            ))}
            
            {(!porRegiao[regiao] || porRegiao[regiao].length === 0) && (
              <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50/50 p-8 text-center dark:border-zinc-600 dark:bg-zinc-800/30">
                <CubeIcon className="h-12 w-12 text-zinc-400 mb-3" />
                <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                  Nenhum pedido
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-1">
                  Esta região está vazia
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
      
      {regioes.length === 0 && (
        <div className="flex h-64 w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50 text-zinc-500 dark:border-zinc-600 dark:bg-zinc-800/50 dark:text-zinc-400">
          <MapPinIcon className="h-16 w-16 text-zinc-400 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhuma região encontrada</h3>
          <p className="text-sm text-center">
            Não há pedidos que correspondam aos filtros aplicados
          </p>
        </div>
      )}
    </div>
  );
};

export default KanbanBoard;